/**
 * The top of the WhatsApp tab: everything that decides whether a message can be
 * sent, and nothing else.
 *
 * ## This file is the fold
 *
 * Slice 16 draws one line through `/integrations`, and it is not "important
 * versus unimportant". It is **does this affect whether you can send?** What
 * answers yes is above the fold, always visible, never behind a disclosure; what
 * answers no is in `WhatsAppAdvanced`. Making that line a module boundary rather
 * than a comment is the point of this file: something can only drift across it
 * by being moved between two files, which is a diff somebody reads, and
 * `verify-whatsapp-tab.tsx` renders this component and asserts what is in it.
 *
 * It renders in the order of what stops a send soonest:
 *
 *   1. **the token's expiry.** The screen exists for this above all else — a
 *      system user token that expires without saying so does not fail loudly;
 *      template submissions start failing, campaigns stop sending, and the first
 *      symptom is a campaign that reached nobody;
 *   2. a stub having answered, which means nothing was verified at all;
 *   3. the check Meta refused, in Meta's own words;
 *   4. every readiness flag that is set;
 *   5. which number this is and how far it reaches.
 *
 * ## No request lives here, deliberately
 *
 * Every import is `@wend/ui`, a display map, a formatter or a type. Nothing
 * touches `lib/api/client`, which reaches `lib/config.ts` and `import.meta.env`
 * and so evaluates only inside Vite — a module that imports it cannot be
 * rendered by a `verify-*` script. The repair button is a `ReactNode` slot for
 * exactly that reason: this component knows how to *say* a token must be
 * replaced, and the panel knows what pressing it does.
 *
 * ## Five shapes for the expiry, not one
 *
 * The old card drew a full Callout for every expiry state, so a healthy
 * connection led with the largest, greenest block on the screen — reassurance
 * given the weight of an alarm, on the panel whose whole argument is that alarms
 * must be impossible to miss. Trouble keeps the Callout and gains the repair;
 * calm is one line. Still on screen, still above the fold, still a statement: a
 * token with no expiry is a decision somebody made, not the absence of
 * information.
 *
 * The three quiet states are why this is not two shapes:
 *
 *   - **a date far enough out** is `never` to `metaTokenExpiry` — meaning
 *     "nothing you need to act on", not "no expiry". The old card printed the
 *     words "no expiry" with a date and a countdown beside them, which is two
 *     claims and one of them false. The date is now printed as a date, with the
 *     warning threshold said out loud so nobody has to guess whether they will
 *     be told;
 *   - **nothing has been read from Meta yet** is not "no expiry" either. Nobody
 *     asked;
 *   - **Meta would not say.** `debug_token` is the one call in a check allowed to
 *     fail without failing the check, and when it does, `expiresAt: null` means
 *     "not reported". `metaTokenInfoSchema`'s own comment requires a screen to
 *     tell that apart from "never expires" rather than paint a green permanent
 *     token. It used to be a footnote *underneath* a green callout — reassurance
 *     on top, retraction below — and it is now amber on its own, because the case
 *     where this warning cannot fire is the case the warning exists for.
 *
 * Expired and expiring read differently on purpose, and not only in colour: one
 * says nothing can be sent, the other says replace it before that happens. They
 * prompt different urgency, so they are different sentences —
 * `META_TOKEN_EXPIRY_DISPLAY` writes both and neither is paraphrased here.
 *
 * ## Flags, not `readiness.state`
 *
 * `metaReadiness`'s own header asks for this: the state is the headline and the
 * flags are the whole story. The old card rendered `META_READINESS_DISPLAY[state]`
 * and one extra callout, so a red quality rating on a number that was also
 * unverified was a rating nobody was shown — `number_not_verified` wins the state
 * outright.
 *
 * ## What a stale snapshot may and may not say
 *
 * A re-check that fails leaves the last success in place on purpose: "failing
 * now, last verified on Tuesday" is true and useful. So the summary strip keeps
 * rendering and gets dated. The readiness callouts do not: a verdict computed
 * from a snapshot the failing check did not manage to replace, printed beside the
 * failure itself, is history dressed as a second fault.
 */
import { META_TOKEN_EXPIRY_WARNING_MS } from '@wend/shared';
import { Callout, Kicker } from '@wend/ui';
import { ShieldCheck } from 'lucide-react';
import type { ReactNode } from 'react';

import type { WhatsAppIntegration } from '../../lib/api/whatsapp';
import { formatDateTime, pluralise } from '../../lib/format';
import {
  META_FAILURE_HEADING,
  META_READINESS_DISPLAY,
  META_STUB_NOTICE,
  META_TOKEN_EXPIRY_DISPLAY,
  META_TOKEN_NOT_INTROSPECTED,
} from '../../lib/whatsapp-display';
import type { WhatsAppReading, WhatsAppSessionCheck } from './whatsapp-reading';

export function WhatsAppSendState({
  integration,
  reading,
  sessionCheck,
  replaceToken,
}: {
  integration: WhatsAppIntegration;
  reading: WhatsAppReading;
  /** This session's own check, if it made one. Null on a first paint. */
  sessionCheck: WhatsAppSessionCheck | null;
  /**
   * The control that starts a token replacement, rendered inside the expiry
   * warning itself. A repair named in a warning and offered three blocks lower
   * is a repair somebody scrolls looking for — which is the complaint this
   * slice exists to answer.
   */
  replaceToken: ReactNode;
}) {
  const { readiness } = reading;

  return (
    <div className="flex flex-col gap-6">
      <TokenExpiry reading={reading} replaceToken={replaceToken} />

      {reading.check?.transport === 'stub' && (
        <Callout tone="warn" title="A stub answered this check">
          {META_STUB_NOTICE}
        </Callout>
      )}

      {sessionCheck?.outcome.ok === false && (
        <Callout tone="bad" title={META_FAILURE_HEADING[sessionCheck.outcome.failure.code]}>
          <p>{sessionCheck.outcome.failure.message}</p>
          <p className="mt-2 font-mono text-[0.72rem] uppercase tracking-[0.1em] text-muted">
            checked {formatDateTime(sessionCheck.outcome.failure.checkedAt)} · transport{' '}
            {sessionCheck.outcome.failure.transport}
          </p>
        </Callout>
      )}

      {sessionCheck === null && integration.lastError !== null && (
        <Callout tone="bad" title="The last check failed">
          {integration.lastError}
        </Callout>
      )}

      {readiness !== null && !reading.stale && (
        <>
          {!readiness.numberVerified && (
            <Callout tone="bad" title={META_READINESS_DISPLAY.number_not_verified.label}>
              {META_READINESS_DISPLAY.number_not_verified.meaning}
            </Callout>
          )}
          {readiness.qualityFlagged && (
            <Callout tone="bad" title={META_READINESS_DISPLAY.quality_flagged.label}>
              {META_READINESS_DISPLAY.quality_flagged.meaning}
            </Callout>
          )}
          {readiness.wabaPendingReview && (
            <Callout tone="warn" title={META_READINESS_DISPLAY.waba_pending_review.label}>
              {META_READINESS_DISPLAY.waba_pending_review.meaning}
            </Callout>
          )}
          {readiness.businessUnverified && (
            <Callout tone="warn" title="This business is not verified with Meta">
              An unverified business can send to a limited number of people a day and cannot raise
              that limit. Verification happens in Meta Business Settings, not in Wend.
            </Callout>
          )}
        </>
      )}

      <AccountSummary integration={integration} reading={reading} />
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Token expiry — the headline                                                 */
/* -------------------------------------------------------------------------- */

const DAY_MS = 86_400_000;

/** Whole days until a moment, rounded down. Negative once it has passed. */
function daysUntil(iso: string): number {
  return Math.floor((Date.parse(iso) - Date.now()) / DAY_MS);
}

/** The warning window, in days, from the constant the API applies. Never retyped. */
const EXPIRY_WARNING_DAYS = Math.round(META_TOKEN_EXPIRY_WARNING_MS / DAY_MS);

function TokenExpiry({
  reading,
  replaceToken,
}: {
  reading: WhatsAppReading;
  replaceToken: ReactNode;
}) {
  const { expiry, expiresAt, expiryKnown, check } = reading;

  if (expiry === 'expired') {
    return (
      <Callout
        tone="bad"
        title={
          <ExpiryTitle
            label={META_TOKEN_EXPIRY_DISPLAY.expired.label.toLowerCase()}
            when={expiresAt === null ? null : formatDateTime(expiresAt)}
          />
        }
        action={replaceToken}
      >
        <p>{META_TOKEN_EXPIRY_DISPLAY.expired.meaning}</p>
      </Callout>
    );
  }

  if (expiry === 'expiring') {
    return (
      <Callout
        tone="warn"
        title={
          <ExpiryTitle
            label={META_TOKEN_EXPIRY_DISPLAY.expiring.label.toLowerCase()}
            // Null only if the API's verdict and the stored date have come
            // apart. The warning is the API's and is shown either way; it is the
            // date that would be missing, not the reason to act.
            when={
              expiresAt === null
                ? null
                : `${formatDateTime(expiresAt)} · ${pluralise(Math.max(daysUntil(expiresAt), 0), 'day')} left`
            }
          />
        }
        action={replaceToken}
      >
        <p>{META_TOKEN_EXPIRY_DISPLAY.expiring.meaning}</p>
      </Callout>
    );
  }

  /*
   * NOTHING READ YET — AND THIS IS THE WHOLE OF WHAT THAT STATE SAYS.
   *
   * `AccountSummary` used to print a second paragraph in this state, opening
   * "Nothing has been read from Meta for this connection yet" and closing "Test
   * the connection to find out" — the same two clauses as this one, immediately
   * underneath it, and between them the only two blocks on the panel. So the
   * unknowns are named together here and `AccountSummary` renders nothing at
   * all, which is also the shorter half of the trade: one paragraph saying three
   * things is what the text rule asks for, and two paragraphs saying one thing
   * each in the same words is what it forbids.
   */
  if (check === null) {
    return (
      <Callout tone="warn" title={<ExpiryTitle label="not checked yet" when={null} />}>
        <p>
          Nothing has been read from Meta for this connection, so when the token stops working —
          along with what this number is rated and how far it reaches — is unknown rather than fine.
          Test the connection to find out.
        </p>
      </Callout>
    );
  }

  /*
   * A date far enough out is `never` to `metaTokenExpiry` — correctly, it means
   * "nothing is coming that you need to act on". It does **not** mean the token
   * has no expiry, and rendering it as the sentence below would be a claim about
   * a token that does in fact stop working. So the date is printed, with the
   * warning threshold said out loud so nobody has to guess whether they will be
   * told. The figure comes off `META_TOKEN_EXPIRY_WARNING_MS`, never retyped: a
   * sentence promising fourteen days beside a constant somebody tuned to seven
   * is a promise this screen would then break.
   */
  if (expiresAt !== null) {
    return (
      <p className="flex flex-wrap items-baseline gap-x-2 gap-y-1 text-[0.9rem] leading-relaxed">
        <ShieldCheck aria-hidden="true" className="size-4 shrink-0 translate-y-0.5 text-good" />
        <span className="text-text">
          Access token — expires {formatDateTime(expiresAt)}, {pluralise(daysUntil(expiresAt), 'day')}{' '}
          away.
        </span>
        <span className="text-muted">
          This turns into a warning here {pluralise(EXPIRY_WARNING_DAYS, 'day')} before it stops
          working.
        </span>
      </p>
    );
  }

  if (!expiryKnown) {
    return (
      <Callout tone="warn" title={<ExpiryTitle label="expiry unknown" when={null} />}>
        <p>{META_TOKEN_NOT_INTROSPECTED}</p>
      </Callout>
    );
  }

  return (
    <p className="flex flex-wrap items-baseline gap-x-2 gap-y-1 text-[0.9rem] leading-relaxed">
      <ShieldCheck aria-hidden="true" className="size-4 shrink-0 translate-y-0.5 text-good" />
      <span className="text-text">Access token — no expiry.</span>
      <span className="text-muted">{META_TOKEN_EXPIRY_DISPLAY.never.meaning}</span>
    </p>
  );
}

function ExpiryTitle({ label, when }: { label: string; when: string | null }) {
  return (
    <span className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
      <span>Access token — {label}</span>
      {when !== null && (
        <span className="font-mono text-[0.78rem] font-normal text-muted">{when}</span>
      )}
    </span>
  );
}

/* -------------------------------------------------------------------------- */
/* Which account this is, and how far it reaches                               */
/* -------------------------------------------------------------------------- */

/**
 * Five fields, each earning its place against the same question the rest of the
 * fold is arranged by.
 *
 * The number and the verified name say *which* account, the first thing anybody
 * checks when something is wrong. The quality rating is Meta's leading indicator
 * of a limit cut. The conversation limit is how many people a day this number can
 * reach at all. And the account review status is the one Meta answer that can be
 * a hard stop with **no readiness state behind it** — `metaReadiness()` folds only
 * `pending` into a state, so a `REJECTED` or `DISABLED` account has no other route
 * onto this screen. That is why it is here and not in Advanced. It prints Meta's
 * word and stops there: what Meta does to a rejected account is not something
 * anybody here has verified, and a confident sentence about it would be folklore.
 *
 * The ids the connection was made with are not here. An id is what you check when
 * reconciling two accounts, not when messages have stopped.
 *
 * **Nothing read yet is a sentence, not five em dashes** — and it is *the fold's*
 * sentence rather than this component's. Every value here comes from a check, so
 * before one has succeeded there is no data, which is a different statement from
 * a value that happens to be missing; the dashes said the wrong one. This block
 * briefly said the right one and said it twice, because `TokenExpiry` says it
 * too and the two paragraphs were adjacent and opened on the same clause. So
 * that sentence now names all three unknowns once, up there, and this returns
 * nothing.
 */
function AccountSummary({
  integration,
  reading,
}: {
  integration: WhatsAppIntegration;
  reading: WhatsAppReading;
}) {
  const { check } = reading;

  /*
   * Nothing to summarise, and nothing to say about that here: `TokenExpiry`
   * above has already said it once, for all three unknowns at once. This used to
   * be a second paragraph opening and closing on the same two clauses, directly
   * under the first, with nothing else on the panel between them. Copy is code
   * on this screen, and saying a thing twice in a row is a defect in it.
   */
  if (check === null) return null;

  return (
    <div>
      <dl className="grid gap-x-8 gap-y-4 sm:grid-cols-2 lg:grid-cols-3">
        {/*
          The row's own copies are the fallback: they are the last successful
          check's values, so a check that failed still leaves the panel saying
          which account this is instead of three em dashes.
        */}
        <Detail label="Phone number">
          {check.phoneNumber.displayPhoneNumber ?? integration.displayPhoneNumber ?? '—'}
        </Detail>
        <Detail label="Verified name">
          {check.phoneNumber.verifiedName ?? integration.verifiedName ?? '—'}
        </Detail>
        <Detail label="Quality rating">
          {check.phoneNumber.qualityRating ?? integration.qualityRating ?? '—'}
        </Detail>
        {/*
          Meta's tier counts conversations that may be *started* in a day, not
          messages — `metaPhoneNumberInfoSchema` says so, and "messaging limit"
          was the wrong unit on a screen where one template message opens a
          conversation. The tier is printed as Meta sends it: a friendly
          translation of a vocabulary Meta extends is a translation that silently
          stops covering the newest value.

          WHOSE LIMIT IT IS CHANGED UNDER US, AND THE LABEL HAD TO FOLLOW.
          Until slice 51 these calls were v21.0 and this was that phone
          number's tier. Meta's 8 October 2025 changelog moved messaging limits
          to the business portfolio and says `messaging_limit_tier` "now returns
          the owning business portfolio's messaging limit" for v24.0 and newer
          requests — so from the moment slice 51 moved the check to v25.0, the
          same field answers a different question. A portfolio holding three
          numbers now shows one figure against all three, and "Conversation
          limit" beside this number's name would read as this number's.

          The number is drawn unchanged; only the label moved, because the
          figure is Meta's and still true — of the portfolio.
        */}
        <Detail label="Portfolio conversation limit">
          {check.phoneNumber.messagingLimitTier ?? '—'}
        </Detail>
        <Detail label="Account review">{check.waba.accountReviewStatus ?? '—'}</Detail>
      </dl>
      <p className="mt-4 text-[0.82rem] leading-relaxed text-muted">
        {reading.stale
          ? `Read from Meta at ${formatDateTime(check.checkedAt)} — the last check that succeeded, not the one failing now.`
          : `Read from Meta at ${formatDateTime(check.checkedAt)}. Nothing here is remembered from documentation.`}
      </p>
    </div>
  );
}

function Detail({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="min-w-0">
      <Kicker as="dt">{label}</Kicker>
      <dd className="mt-1 break-words text-[0.92rem] text-text">{children}</dd>
    </div>
  );
}
